import { buildIlikeFilter, buildInFilter, combineAndFilters, combineOrFilters, quoteRsqlValue } from "../rsql";
import type { IncidentFolderId } from "../../layouts/incidents/lib/constants";
import type { IncidentFilters } from "../../layouts/incidents/lib/filterUtils";

export function buildIncidentReadSearchFilter(
  filters: Pick<
    IncidentFilters,
    | "openSearchId"
    | "selectedPriority"
    | "selectedStatus"
    | "selectedCustomerId"
  >,
  customerSourceSystemIds?: string[] | null,
): string | undefined {
  if (filters.selectedCustomerId) {
    if (customerSourceSystemIds == null) {
      return undefined;
    }
    if (customerSourceSystemIds.length === 0) {
      return "__NO_MATCH__";
    }
  }

  return combineAndFilters(
    buildIlikeFilter("openSearchId", filters.openSearchId),
    filters.selectedPriority !== "all"
      ? buildIlikeFilter("priority", filters.selectedPriority)
      : undefined,
    filters.selectedStatus !== "all"
      ? buildIlikeFilter("status", filters.selectedStatus)
      : undefined,
    filters.selectedCustomerId
      ? buildInFilter("sourceSystemId", customerSourceSystemIds ?? [])
      : undefined,
  );
}

function buildFolderFilter(
  folder: IncidentFolderId,
  username: string | null,
): string | undefined | "__NO_MATCH__" {
  switch (folder) {
    case "inbox": {
      const value = username?.trim();
      if (!value) return "__NO_MATCH__";
      return `assignedTo==${quoteRsqlValue(value)}`;
    }
    case "group":
      return "assignedGroup=isnull=false";
    case "anomalies":
      return "detectedAnomalyEvents=isnull=false";
    case "new":
      return "status==NEW";
    case "critical":
      return combineOrFilters("priority==CRITICAL", "priority==CRISIS", "priority==Critical");
    case "resolved":
      return combineOrFilters("status==RESOLVED", "status==CLOSED");
    case "all":
    default:
      return undefined;
  }
}

function buildTextSearchFilter(term: string): string | undefined {
  const trimmed = term.trim();
  if (!trimmed) return undefined;
  return combineOrFilters(
    buildIlikeFilter("title", trimmed),
    buildIlikeFilter("description", trimmed),
    buildIlikeFilter("id", trimmed),
    buildIlikeFilter("category", trimmed),
    buildIlikeFilter("assignedTo", trimmed),
  );
}

export function buildIncidentListSearchFilter(options: {
  filters: IncidentFilters;
  folder: IncidentFolderId;
  username: string | null;
  customerSourceSystemIds?: string[] | null;
}): string | undefined | "__NO_MATCH__" {
  const { filters, folder, username, customerSourceSystemIds } = options;

  if (filters.selectedCustomerId && customerSourceSystemIds == null) {
    return undefined;
  }

  if (
    filters.selectedCustomerId &&
    customerSourceSystemIds != null &&
    customerSourceSystemIds.length === 0
  ) {
    return "__NO_MATCH__";
  }

  const folderFilter = buildFolderFilter(folder, username);
  if (folderFilter === "__NO_MATCH__") {
    return "__NO_MATCH__";
  }

  return (
    combineAndFilters(
      folderFilter,
      buildTextSearchFilter(filters.searchTerm),
      buildIlikeFilter("openSearchId", filters.openSearchId),
      filters.selectedPriority !== "all"
        ? buildIlikeFilter("priority", filters.selectedPriority)
        : undefined,
      filters.selectedStatus !== "all"
        ? buildIlikeFilter("status", filters.selectedStatus)
        : undefined,
      filters.selectedAssignee !== "all"
        ? `assignedTo==${quoteRsqlValue(filters.selectedAssignee)}`
        : undefined,
      filters.selectedGroup !== "all"
        ? `assignedGroup==${quoteRsqlValue(filters.selectedGroup)}`
        : undefined,
      filters.selectedCategory !== "all"
        ? `category==${quoteRsqlValue(filters.selectedCategory)}`
        : undefined,
      filters.selectedCustomerId
        ? buildInFilter("sourceSystemId", customerSourceSystemIds ?? [])
        : undefined,
      filters.dateRange.start ? `createdDate=ge=${filters.dateRange.start}` : undefined,
      filters.dateRange.end ? `createdDate=le=${filters.dateRange.end}` : undefined,
    ) ?? ""
  );
}
